/**
 * Sight word deck — flash a word, she says it, the deck records how fast.
 *
 * The point is automaticity, not accuracy: decoding "the" letter by letter is a
 * miss even when the answer is right. `instantThresholdMs` is where that line
 * sits, and the deck reports which words cleared it.
 */

import { z } from 'zod'
import { NonEmptyString, SeedSchema, specFor } from '../common'

export const SIGHT_WORD_STATUSES = ['new', 'practicing', 'automatic'] as const

export const SightWordSchema = z.object({
  text: NonEmptyString.max(24),
  /** Where this word stood last time, so the deck can lead with what is close. */
  status: z.enum(SIGHT_WORD_STATUSES).default('new'),
  /** Best recognition time so far, for "you were quicker that time". */
  bestMs: z.number().int().min(0).max(60_000).optional(),
})

export const SightWordDeckConfigSchema = z
  .object({
    words: z.array(SightWordSchema).min(1).max(30),
    /** How long the card stays up before it moves on. */
    flashDurationMs: z.number().int().min(300).max(15_000),
    requireInstantRecognition: z.boolean(),
    /** Answered inside this window counts as instant. */
    instantThresholdMs: z.number().int().min(300).max(10_000).default(1_500),
    ordering: z.enum(['fixed', 'shuffled']),
    shuffleSeed: SeedSchema.default(1),
    /** Say it aloud, then it appears — pairs the sound with the shape. */
    echoPrompt: z.boolean().default(true),
    /** Two passes through the deck build the repetition the method depends on. */
    passes: z.number().int().min(1).max(4).default(1),
  })
  .superRefine((config, ctx) => {
    const texts = config.words.map((word) => word.text.toLowerCase())
    if (new Set(texts).size !== texts.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['words'], message: 'the same word appears twice in the deck' })
    }
    if (config.requireInstantRecognition && config.instantThresholdMs > config.flashDurationMs) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['instantThresholdMs'],
        message: 'the instant window is longer than the card is on screen, so nothing can ever miss it',
      })
    }
  })

export type SightWordDeckConfig = z.infer<typeof SightWordDeckConfigSchema>

export const SightWordDeckSpecSchema = specFor('sight_word_deck', SightWordDeckConfigSchema)
